import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Campaigns } from '@/components/pages/channels/campaigns';
import { useChannelsStore } from '@/components/pages/channels/use-channels-store';
import { Typography } from '@/components/ui/typography';
import { useAuthContext } from '@/contexts/auth-context';
import { useOpeningContext } from '@/contexts/opening-context';
import { useCampaignRefetch } from '@/hooks/use-campaign-refetch';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

import { useChannelColumns } from './campaigns/columns';
import { InactiveChannels } from './inactive-channels';
import { SourcingOpenings } from './sourcing-openings';

export function ChannelsSection() {
  const openingSlug = useOpeningSlug();
  const setLastAutomationChannel = useChannelsStore(
    s => s.setLastAutomationChannel,
  );
  const setActiveCampaigns = useChannelsStore(s => s.setActiveCampaigns);
  const activeCampaigns = useChannelsStore(s => s.activeCampaigns);
  const {
    hiringGoal,
    currentHiringGoalId,
    showAggregateSpend,
    showAggregateHires,
  } = useOpeningContext();
  const { canPostToJobBoards } = useAuthContext();

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.list,
      openingSlug,
      currentHiringGoalId,
      showAggregateHires,
      showAggregateSpend,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSourcingChannels(
        openingSlug,
        currentHiringGoalId,
        showAggregateHires,
        showAggregateSpend,
      ),
  });

  useCampaignRefetch(async () => {
    await refetch();
  });

  if (isError) {
    throw new Error('Error fetching channels');
  }

  const columns = useChannelColumns({
    brand: data?.brand_slug ?? 'no_brand',
    location: data?.location_id ?? '',
    funnel: data?.opening_id ?? '',
  });

  const campaigns = useMemo(() => data?.active_channels ?? [], [data]);

  useEffect(() => {
    setActiveCampaigns(
      campaigns.filter(
        campaign =>
          campaign.status === 'active' || campaign.status === 'stopped',
      ),
    );
  }, [campaigns, setActiveCampaigns]);

  const ongoingCampaigns = useMemo(() => {
    const other =
      hiringGoal?.is_current && data?.other_channel ? [data.other_channel] : [];
    return [...activeCampaigns, ...other];
  }, [activeCampaigns, data?.other_channel, hiringGoal?.is_current]);

  const scheduledCampaigns = useMemo(
    () => campaigns.filter(campaign => campaign.status === 'scheduled'),
    [campaigns],
  );

  const completedCampaigns = useMemo(() => {
    const filteredCompleted = campaigns.filter(
      campaign => campaign.status === 'completed',
    );

    const other =
      !hiringGoal?.is_current && data?.other_channel
        ? [data.other_channel]
        : [];
    return [...filteredCompleted, ...other];
  }, [campaigns, hiringGoal?.is_current, data?.other_channel]);

  const inactiveChannels = useMemo(() => data?.inactive_channels ?? [], [data]);
  const openingId = useMemo(() => data?.opening_id, [data]);

  useEffect(() => {
    setLastAutomationChannel(ongoingCampaigns);
  }, [campaigns, ongoingCampaigns, openingId, setLastAutomationChannel]);

  return (
    <>
      <SourcingOpenings />

      {(isFetching || ongoingCampaigns.length > 0) && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage defaultMessage="Ongoing campaigns" />
          </Typography>
          <Campaigns
            campaigns={ongoingCampaigns}
            columns={columns}
            isLoading={isFetching}
          />
        </>
      )}

      {(isFetching || scheduledCampaigns.length > 0) && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage defaultMessage="Scheduled campaigns" />
          </Typography>
          <Campaigns
            campaigns={scheduledCampaigns}
            columns={columns}
            isLoading={isFetching}
          />
        </>
      )}

      {(isFetching || completedCampaigns.length > 0) && (
        <>
          <Typography as="h2" variant="caption">
            <FormattedMessage defaultMessage="Completed campaigns" />
          </Typography>
          <Campaigns
            campaigns={completedCampaigns}
            columns={columns}
            isLoading={isFetching}
          />
        </>
      )}

      {canPostToJobBoards && (
        <InactiveChannels
          channels={inactiveChannels}
          openingId={openingId}
          isLoading={isFetching}
        />
      )}
    </>
  );
}
